import { useDraggable } from '@dnd-kit/core';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { Box, styled } from '@mui/material';

import { ELayoutManagerMode } from '#shared';

import { useAppDispatch } from '../../redux-store';
import { widgetManagerActions, type WidgetId } from '../../widgetManager';

import type { ControlsProps } from '#shared';

const PREFIX = 'TSMED-WidgetDragger';
export const widgetDraggerClasses = {
  root: `${PREFIX}-root`,
  icon: `${PREFIX}-icon`,
};

const Root = styled(Box)(({ theme: { palette } }) => ({
  [`&.${widgetDraggerClasses.root}`]: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    boxSizing: 'border-box',
    cursor: 'grab',
    verticalAlign: 'middle',
    background: 'rgba(29, 42, 55, 0.50)',
    flex: '0 0 auto',
    fontSize: '1.5rem',
    padding: '8px',
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    backgroundColor: '#1D2A37',
    border: '2px solid #364B5F',
    borderRadius: '12px',
    color: '#fff',
    width: '40px',
    height: '40px',
    overflow: 'hidden',

    '& svg': {
      fill: palette.common.white,
    },

    '&:hover': {
      backgroundColor: palette.mapBlue[800],
    },

    '&.Mui-disabled': {
      opacity: 0.3,
      color: palette.common.white,
    },
  },
}));

/** Controls the popover can render (strictly typed). */
export type ControlMap = Record<string, ControlsProps>;

interface WidgetDraggerProps {
  instanceId: WidgetId;
}

export const WidgetDragger = ({ instanceId }: WidgetDraggerProps) => {
  const dispatch = useAppDispatch();
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: instanceId,
  });
  const handleGrabStart = (e: React.MouseEvent | React.TouchEvent) => {
    dispatch(
      widgetManagerActions.setLayoutManagerMode(ELayoutManagerMode.EDIT),
    );

    listeners?.onPointerDown?.(e);
    e.stopPropagation();
  };
  const handleGrabComplete = (e: React.MouseEvent | React.TouchEvent) => {
    dispatch(
      widgetManagerActions.setLayoutManagerMode(ELayoutManagerMode.VIEW),
    );

    e.stopPropagation();
  };

  return (
    <Root
      className={widgetDraggerClasses.root}
      onPointerUp={handleGrabComplete}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onPointerDown={handleGrabStart}
    >
      <DragIndicatorIcon className={widgetDraggerClasses.icon} />
    </Root>
  );
};
